import { ArrowUpRight, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import sbcLogo from "@/assets/sbc-logo-transparent-1200x400.png";

export function HeroSection() {
  return (
    <section className="relative overflow-hidden border-b border-border bg-gradient-paper">
      <div className="container grid grid-cols-1 gap-12 py-16 md:py-20 lg:grid-cols-12 lg:gap-16 lg:py-28">
        <div className="flex flex-col gap-8 lg:col-span-7">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              <span className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                NDIS 0138 Readiness · Diagnostic
              </span>
            </span>
            <span className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Document No. 0138 / R.E.A.D.Y. — v0.1
            </span>
          </div>

          <div className="space-y-6">
            <h1 className="font-display text-4xl font-medium leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Understand your 0138 readiness <span className="text-accent">before the auditor does.</span>
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              A short, governed intake for NDIS providers. Receive a preliminary readiness snapshot,
              identified evidence gaps, operational risk indicators and a 30 / 60 / 90 day action plan —
              without sharing participant identifiers, medical histories or private worker information.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="rounded-sm px-6 text-[13px] font-medium tracking-wide shadow-paper">
              <Link to="/intake">
                Begin readiness intake
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              size="lg"
              className="rounded-sm px-4 text-[13px] font-medium tracking-wide text-foreground hover:bg-secondary"
            >
              <a href="#framework">Review the R.E.A.D.Y. framework</a>
            </Button>
          </div>

          <div className="flex flex-col gap-3 rounded-sm border border-border bg-card/60 p-4 sm:flex-row sm:items-start sm:gap-4">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
            <div className="space-y-1">
              <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Guidance only · Not legal advice, audit certification or registration approval
              </p>
              <p className="text-sm leading-relaxed text-foreground/85">
                The intake runs in your browser. Nothing is sent to the server unless Save to Server is selected. Saved records are de-identified provider snapshots available only to the owner through protected administration.
              </p>
            </div>
          </div>
        </div>

        <div className="relative lg:col-span-5">
          <div className="relative overflow-hidden rounded-sm border border-border bg-foreground shadow-elevated">
            <div className="flex h-[420px] flex-col items-center justify-center gap-6 px-8 pt-10 pb-48 text-center sm:h-[520px] sm:pb-44 lg:h-[600px] lg:pb-48">
              <img
                src={sbcLogo}
                alt="Spiral Beyond Compliance"
                className="max-h-[60%] max-w-full object-contain"
                loading="eager"
                data-testid="hero-brand-logo"
              />
              <p
                className="font-display max-w-[22ch] text-balance text-base leading-snug tracking-tight text-background/85 sm:text-lg"
                data-testid="hero-brand-motto"
              >
                From evidence gaps to clear next steps.
              </p>
            </div>
            <div className="absolute inset-x-4 bottom-4 flex flex-col gap-2 rounded-sm border border-border bg-card/95 p-4 backdrop-blur-md">
              <p className="eyebrow">Issue · 0138.1</p>
              <p className="font-display text-base leading-tight text-foreground">
                A short, governed intake for providers preparing for the next NDIS readiness cycle.
              </p>
              <div className="flex items-center justify-between pt-1 text-[11px] text-muted-foreground">
                <span className="font-mono-label uppercase tracking-[0.18em]">Preliminary</span>
                <span className="font-mono-label uppercase tracking-[0.18em]">Not a Commission outcome</span>
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 divide-x divide-border rounded-sm border border-border bg-card text-center">
            {[
              { k: "9", label: "Sections" },
              { k: "Guided", label: "Consultant-paced" },
              { k: "0", label: "Identifiers Asked" }
            ].map((stat) => (
              <div key={stat.label} className="px-3 py-4">
                <div className="font-display text-2xl font-medium text-foreground">{stat.k}</div>
                <div className="eyebrow mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
